/**
 * admin-doctor-filter.js — 後台「醫師管理」卡片網格篩選控制器（/admin/doctors）
 *
 * doctors.html 工具列為「狀態 tabs（全部 / 已通過 / 送審中 / 需修改）+ 搜尋框」，
 * 卡片網格（#doctor-grid > .doctor-card[data-status]）需依 tab 過濾、依關鍵字即時篩。
 * 此前 tabs 只有靜態標記（data-doctor-grid-filter，admin-table.js 不接），點擊不過濾。
 *
 * 行為與前台 clinics / articles 列表同源 —— 複用 list-filter.js 引擎（過濾 predicate +
 * 委派 + 空狀態切換 + pagehide 釋放），差異僅在「觸發元件是 tabs 而非 chips」：
 *   - chipSelector '.tabs__item' + filterAttr 'data-filter-value'（admin-table.js 同源慣例）。
 *   - 分類維度 = 審核狀態，比對 .doctor-card[data-status]（approved / pending / revision）。
 *   - 關鍵字比對醫師姓名 + 科別 + 學經歷 + 專業領域，清空回到只受 tab 篩選。
 *   - 篩選 0 筆顯示「找不到符合的醫師」空態（#doctor-empty-no-result）；
 *     其「清除篩選」鈕重置回「全部」tab（兼清關鍵字），與空態文案承諾一致。
 *
 * 設計：root 守門早退，找不到 tabs / grid 即 return，非該頁不執行、不報錯。
 * 載入：由 src/admin.js import（vite.config 全域注入硬寫 /src/main.js，後台頁 inline script
 *       會被剝離，故所有後台業務 JS 一律掛 admin.js 鏈）。
 */

import { createListFilter } from './list-filter.js';

const SELECTORS = {
  tabs: '[data-doctor-grid-filter]',
  tab: '.tabs__item',
  grid: '#doctor-grid',
  card: '.doctor-card',
  search: '.admin-toolbar .admin-search__input',
  empty: '#doctor-empty-no-result',
  emptyReset: '#doctor-empty-no-result .empty-state__action button',
};

/** 取卡片可搜尋文字：姓名 + 科別 + 學經歷 + 專業領域，組成單一小寫字串供 includes 比對。 */
function buildSearchText(card) {
  const parts = [
    card.querySelector('.doctor-card__name'),
    card.querySelector('.doctor-card__specialty'),
    card.querySelector('.doctor-card__bio'),
    card.querySelector('.doctor-card__field'),
  ].map((el) => (el ? el.textContent : ''));
  return parts.join(' ').toLowerCase();
}

function init() {
  const tabsRoot = document.querySelector(SELECTORS.tabs);
  const grid = document.querySelector(SELECTORS.grid);
  if (!tabsRoot || !grid) return null;

  const tabs = Array.from(tabsRoot.querySelectorAll(SELECTORS.tab));
  const cards = Array.from(grid.querySelectorAll(SELECTORS.card));
  if (!tabs.length || !cards.length) return null;

  const filter = createListFilter({
    chipsRoot: tabsRoot,
    chips: tabs,
    chipSelector: SELECTORS.tab,
    filterAttr: 'data-filter-value',
    searchInput: document.querySelector(SELECTORS.search),
    cards,
    emptyEl: document.querySelector(SELECTORS.empty),
    getCategory: (card) => card.getAttribute('data-status') || '',
    getSearchText: buildSearchText,
    // 預設 setVisible 走 card.hidden；CSS 已補 .doctor-card[hidden]{display:none} 守衛
    // （卡片 display:flex，否則 UA [hidden] 被蓋過 → 設 hidden 視覺無效）。
  });
  if (!filter) return null;

  // 空態「清除篩選」：回到「全部」tab（其 data-filter-value='all'）並清關鍵字，
  // 直接模擬點擊「全部」tab 走既有委派路徑（單一事實源，不另寫一套重置邏輯）。
  const resetBtn = document.querySelector(SELECTORS.emptyReset);
  const allTab = tabs.find((t) => (t.getAttribute('data-filter-value') || '') === 'all');
  const searchInput = document.querySelector(SELECTORS.search);
  if (resetBtn && allTab) {
    const onReset = () => {
      // 先清關鍵字：value 清空後須主動派發 input 事件，讓 list-filter 內部 keyword 同步歸零
      //（程式設定 value 不會觸發 input；否則清了框但 keyword 仍卡舊值）。再點「全部」tab 切回全分類。
      if (searchInput) {
        searchInput.value = '';
        searchInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
      allTab.click();
    };
    resetBtn.addEventListener('click', onReset);
    window.addEventListener('pagehide', () => resetBtn.removeEventListener('click', onReset), { once: true });
  }

  return { filter };
}

// module script 為 defer：DOM 已解析。仍守 readyState 以相容非 module 載入情境。
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
